import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DropResult } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { useReorderPpps } from "@/hooks/usePppMutations";
import { ApiError } from "@/lib/apiClient";
import { reorderPermutation } from "@/lib/reorder";
import { PaginatedResponse, Ppp, PppFilter } from "@/types";

/** Optimistic drag-to-rank for the unpaginated /ppp list; the server re-derives every deliverable order from it. */
export function usePppReorder(ppps: Ppp[], params: PppFilter) {
  const qc = useQueryClient();
  const reorder = useReorderPpps();

  const onDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) return;

    const { next, items } = reorderPermutation(ppps, source.index, destination.index);
    // An in-flight refetch from a previous drag would overwrite this optimistic order.
    qc.cancelQueries({ queryKey: ["ppps"] });
    qc.setQueryData<PaginatedResponse<Ppp>>(["ppps", params], (old) =>
      old ? { ...old, data: next.map((p, i) => ({ ...p, orderIndex: i })) } : old,
    );
    reorder.mutate(items, {
      // The server insists on every PPP exactly once; a stale list (someone added or removed a PPP
      // since it loaded) is the only way that fails, and the settled invalidation already refreshes it.
      onError: (error) =>
        toast.error(
          error instanceof ApiError && error.body?.message === "PPP_REORDER_INCOMPLETE"
            ? "The PPP list changed underneath you — it has been refreshed, drag again"
            : "Could not save the new ranking",
        ),
    });
  }, [ppps, params, qc, reorder]);

  // Drag stays off while a permutation is in flight so an older response cannot land last.
  return { onDragEnd, isPending: reorder.isPending };
}
